export type Padding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type ButtonSize = 'sm' | 'md' | 'lg';

export type CornerRadius = {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
};

export type BorderStyle = {
  width: Padding;
  style: 'none' | 'solid' | 'dashed' | 'dotted';
  color: string;
};

export type Align = 'left' | 'center' | 'right';

export type ImageHeightMode = 'auto' | 'fillColumn' | 'fixed';

export type ImageObjectFit = 'cover' | 'contain';

export type ImageIntrinsicSize = {
  width: number;
  height: number;
};

export type FacilityLogoSizing = 'header';

export type ImageOverlay = {
  enabled: boolean;
  color: string;
  opacity: number;
};

export type TextVariant = 'normal' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type EmailTextDirection = 'ltr' | 'rtl' | 'auto';

export type EmailFontFamily =
  | 'Arial, Helvetica, sans-serif'
  | 'Georgia, serif'
  | "'Times New Roman', Times, serif"
  | "'Trebuchet MS', Helvetica, sans-serif"
  | 'Verdana, Geneva, sans-serif'
  | "'Courier New', Courier, monospace"
  | 'Tahoma, Geneva, sans-serif'
  | 'Helvetica, Arial, sans-serif';

export type FacilityAssetKey = 'logo' | 'logoDark' | 'defaultHero';

export type MediaRef =
  | { kind: 'facility'; assetKey: FacilityAssetKey }
  | { kind: 'library'; mediaId: string }
  | { kind: 'url'; url: string };

export type SocialNetwork = 'facebook' | 'instagram' | 'twitter';

export type SocialBlockLinkNetwork = SocialNetwork | 'custom';

export type SocialBlockLink = {
  id: string;
  network: SocialBlockLinkNetwork;
  title?: string;
  alt?: string;
  url: string;
  icon?: MediaRef | null;
};

export type SocialIconVariant = 'white';

type BaseBlock = {
  id: string;
};

export type TextBlock = BaseBlock & {
  type: 'text';
  variant: TextVariant;
  html: string;
  align: Align;
  widthPx?: number | null;
  color?: string | null;
  linkColor?: string | null;
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
  fontFamily?: EmailFontFamily | null;
  padding: Padding;
}

export type ImageBlock = BaseBlock & {
  type: 'image';
  media: MediaRef | null;
  alt: string;
  href: string;
  align: Align;
  facilityLogoSizing?: FacilityLogoSizing | null;
  widthPx?: number | null;
  heightMode: ImageHeightMode;
  heightPx: number | null;
  objectFit: ImageObjectFit;
  intrinsicSize: ImageIntrinsicSize | null;
  borderRadius: CornerRadius;
  fullWidthMobile: boolean;
  imageOverlay: ImageOverlay;
  padding: Padding;
}

export type ButtonBlock = BaseBlock & {
  type: 'button';
  text: string;
  href: string;
  align: Align;
  backgroundColor?: string | null;
  color?: string | null;
  borderColor?: string | null;
  borderWidth: number;
  fontSize: number;
  fontWeight: number;
  borderRadius: CornerRadius;
  size: ButtonSize;
  fullWidth: boolean;
  fullWidthMobile: boolean;
  padding: Padding;
}

export type ButtonChrome = {
  backgroundColor?: string | null;
  color?: string | null;
  borderColor?: string | null;
  borderWidth: number;
  borderRadius: CornerRadius;
}

export type ButtonSizing = {
  fontSize: number;
  fontWeight: number;
  size: ButtonSize;
}

export type ButtonRowSlot = ButtonChrome & ButtonSizing & {
  label: string;
  href: string;
  fullWidth: boolean;
}

export type ButtonRowBlock = BaseBlock & {
  type: 'buttonRow';
  buttons: [ButtonRowSlot, ButtonRowSlot];
  align: Align;
  gap: number;
  stackOnMobile: boolean;
  stackFullWidth: boolean;
  padding: Padding;
}

export type DividerBlock = BaseBlock & {
  type: 'divider';
  color?: string | null;
  thickness: number;
  style: 'solid' | 'dashed' | 'dotted';
  widthPct: number;
  padding: Padding;
}

export type SpacerBlock = BaseBlock & {
  type: 'spacer';
  height: number;
}

export type SocialBlock = BaseBlock & {
  type: 'social';
  links: SocialBlockLink[];
  iconVariant: SocialIconVariant;
  align: Align;
  iconSize: number;
  gap: number;
  padding: Padding;
}

export type EyebrowRule = 'none' | 'before' | 'after' | 'both';

export type EyebrowTextTransform =
  'none' | 'uppercase' | 'lowercase' | 'capitalize';

export type EyebrowBlock = BaseBlock & {
  type: 'eyebrow';
  text: string;
  rule: EyebrowRule;
  fontWeight?: number;
  fontSize?: number;
  textTransform?: EyebrowTextTransform;
  color?: string | null;
  ruleColor?: string | null;
  padding: Padding;
}

export type Block =
  | TextBlock
  | ImageBlock
  | ButtonBlock
  | DividerBlock
  | SpacerBlock
  | SocialBlock
  | EyebrowBlock
  | ButtonRowBlock;

export type BlockType = Block['type'];

export type BlockOf<T extends BlockType> = Extract<Block, { type: T }>;

export type Column = {
  id: string;
  backgroundColor: string;
  backgroundImage: string;
  backgroundImageOverlay: ImageOverlay;
  padding: Padding;
  border: BorderStyle;
  borderRadius: CornerRadius;
  verticalAlign: 'top' | 'middle' | 'bottom';
  blocks: Block[];
}

export type Row = {
  id: string;
  backgroundColor: string;
  backgroundImage: string;
  backgroundImageOverlay: ImageOverlay;
  padding: Padding;
  border: BorderStyle;
  borderRadius: CornerRadius;
  columns: Column[];
  columnWidths: number[];
  columnGap: number;
  responsive: boolean;
  contentGutter?: boolean;
}

export type Group = {
  id: string;
  backgroundColor: string;
  backgroundImage: string;
  backgroundImageOverlay: ImageOverlay;
  padding: Padding;
  border: BorderStyle;
  borderRadius: CornerRadius;
  rows: Row[];
}

export type DocumentSection = Row | Group;

export type EmailSettings = {
  contentWidth: number;
  pageBackgroundColor: string;
  contentBackgroundColor: string;
  pagePadding: Padding;
  headingFontFamily: EmailFontFamily;
  bodyFontFamily: EmailFontFamily;
  headingColor: string;
  paragraphColor: string;
  linkColor: string;
  dividerColor: string;
  buttonFillColor: string;
  buttonTextColor: string;
  buttonBorderColor: string;
  lang: string;
  dir: EmailTextDirection;
}

export const EMAIL_SETTINGS_COLOR_KEYS = [
  "headingColor",
  "paragraphColor",
  "buttonFillColor",
  "buttonTextColor",
  "buttonBorderColor",
  "linkColor",
  "dividerColor",
  "pageBackgroundColor",
  "contentBackgroundColor",
] as const satisfies readonly (keyof EmailSettings)[];

export type EmailSettingsColorKey = (typeof EMAIL_SETTINGS_COLOR_KEYS)[number];

export type EmailSettingsColors = Pick<EmailSettings, EmailSettingsColorKey>;

export type EmailDocument = {
  schemaVersion: number;
  settings: EmailSettings;
  sections: DocumentSection[];
}

export const EMAIL_DOCUMENT_SCHEMA_VERSION = 1;

export type Selection =
  | { kind: 'email' }
  | { kind: 'group'; groupId: string }
  | { kind: 'row'; rowId: string }
  | { kind: 'column'; rowId: string; columnId: string }
  | { kind: 'block'; rowId: string; columnId: string; blockId: string }
  | {
      kind: 'buttonRowSlot';
      rowId: string;
      columnId: string;
      blockId: string;
      slotIndex: number;
    };

export type TextBlockTarget = {
  rowId: string;
  columnId: string;
  blockId: string;
  slotIndex?: number;
};

export type EditorMode = 'design' | 'preview';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export type TemplateId = 'blank';
